/**
 * The word diff behind the Edit & send panel.
 *
 * Display-only: what gets STORED is the raw draft/final pair (the server owns
 * that), this just lets the editor see what they are teaching while they type.
 * Word-level rather than character-level because an email edit is read in
 * words, and a char diff of "3–5 days" → "24 hours" is confetti.
 *
 * Plain LCS over word tokens. Support replies are a few hundred words, so the
 * quadratic table is nowhere near mattering; the cap below is a guard against
 * pasting a novel, not a performance target.
 */

export interface DiffToken {
  kind: 'same' | 'del' | 'add';
  text: string;
}

const MAX_WORDS = 2000;

/** Split into words, folding each word's trailing whitespace into its token. */
function tokenize(text: string): string[] {
  return text.match(/\S+\s*/g) ?? [];
}

export function diffWords(draft: string, final: string): DiffToken[] {
  const a = tokenize(draft);
  const b = tokenize(final);

  // Past the cap the table gets big and the visual diff stops being readable
  // anyway; show it as a wholesale replacement instead of freezing the tab.
  if (a.length > MAX_WORDS || b.length > MAX_WORDS) {
    return [
      { kind: 'del', text: draft },
      { kind: 'add', text: final },
    ];
  }

  // LCS lengths, then walk back. Compared trimmed so "days." and "days. " are
  // the same word — whitespace differences are not edits.
  const same = (x: string, y: string) => x.trim() === y.trim();
  const table: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );
  for (let i = a.length - 1; i >= 0; i -= 1) {
    for (let j = b.length - 1; j >= 0; j -= 1) {
      table[i]![j] = same(a[i]!, b[j]!)
        ? table[i + 1]![j + 1]! + 1
        : Math.max(table[i + 1]![j]!, table[i]![j + 1]!);
    }
  }

  const tokens: DiffToken[] = [];
  const push = (kind: DiffToken['kind'], text: string) => {
    const last = tokens[tokens.length - 1];
    if (last && last.kind === kind) last.text += text;
    else tokens.push({ kind, text });
  };

  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (same(a[i]!, b[j]!)) {
      // The final's spelling wins for 'same' runs so the rendered text matches
      // what will actually be sent.
      push('same', b[j]!);
      i += 1;
      j += 1;
    } else if (table[i + 1]![j]! >= table[i]![j + 1]!) {
      push('del', a[i]!);
      i += 1;
    } else {
      push('add', b[j]!);
      j += 1;
    }
  }
  while (i < a.length) push('del', a[i++]!);
  while (j < b.length) push('add', b[j++]!);

  return tokens;
}
